import {
  Alert,
  Box,
  Form,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { FinanceValueObjectsBudgetPeriodicityEnum } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'

import { periodicityOptions } from 'modules/finance/lib/budgets'
import {
  WizardData,
  WizardDataField,
  validateMandatoryFIels,
} from 'modules/finance/lib/newBudgetWizard'

interface ValidateProps {
  data: WizardData
  onDataChanged: (data: WizardData) => void
}
const validate = async ({ data, onDataChanged }: ValidateProps) => {
  const mandatoryFields: WizardDataField[] = ['effectiveFrom', 'periodicity', 'amountCents']

  const { invalidFields, dataWithErrors } = validateMandatoryFIels(mandatoryFields, data)

  onDataChanged(dataWithErrors)

  if (invalidFields.length) {
    return Promise.reject()
  }

  if (!isNumber(data.amountCents) || Number(data.amountCents) <= 0) {
    onDataChanged({
      ...dataWithErrors,
      errors: {
        amountCents: i18n.t('budgets.forms.errors.invalid_amount'),
      },
    })
    return Promise.reject()
  }

  if (data.effectiveUntil && moment(data.effectiveFrom).isAfter(moment(data.effectiveUntil))) {
    onDataChanged({
      ...dataWithErrors,
      errors: {
        effectiveFrom: i18n.t('budgets.forms.errors.start_date'),
      },
    })
    return Promise.reject()
  }
}
const BudgetLimits = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  useOnNext(() => validate({ data, onDataChanged }))
  return (
    <Box gap="s40" flexDirection="column">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>{i18n.t('budgets.forms.new_budget.budget_limits.title')}</Text.Header2>

          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_limits.description')}</Text.Normal>
        </Box>
      </Box>
      <Box flexDirection="row" gap="s24" justifyContent="spaceBetween">
        <Box gap="s40" maxWidth="s600">
          <Form padding="s0" notRounded>
            <Box flexDirection="row" gap="s16">
              <DayPicker
                mandatory
                valueFormat="backend_datetime"
                mode="single"
                error={data.errors?.effectiveFrom}
                value={data.effectiveFrom}
                placeholder={i18n.t(
                  'budgets.forms.new_budget.budget_limits.start_date_placeholder'
                )}
                onChange={(value) => onDataChanged({ ...data, effectiveFrom: value })}
                label={i18n.t('budgets.forms.new_budget.budget_limits.start_date')}
              />
              <DayPicker
                valueFormat="backend_datetime"
                mode="single"
                value={data.effectiveUntil}
                minDate={moment(data.effectiveFrom).startOf('day')}
                onChange={(value) => onDataChanged({ ...data, effectiveUntil: value })}
                label={i18n.t('budgets.forms.new_budget.budget_limits.end_date')}
                placeholder={i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder')}
              />
            </Box>
            <Box flexDirection="row" gap="s16" style={{ overflowWrap: 'normal' }}>
              <SelectField
                mandatory
                none={i18n.t('budgets.forms.new_budget.budget_limits.select_time_frame')}
                error={data.errors?.periodicity}
                label={i18n.t('budgets.forms.new_budget.budget_limits.time_frame')}
                options={periodicityOptions()}
                value={data.periodicity}
                onChange={(value) =>
                  onDataChanged({
                    ...data,
                    periodicity: value as FinanceValueObjectsBudgetPeriodicityEnum,
                  })
                }
              />
              <MoneyField
                error={data.errors?.amountCents}
                mandatory
                currency={data.currency}
                label={i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame')}
                value={data.amountCents}
                placeholder="0"
                onChange={(value) => onDataChanged({ ...data, amountCents: value })}
              />
            </Box>
          </Form>
        </Box>
        <Box width="s300">
          <Alert color="info">
            <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_limits.hint')}</Text.Normal>
          </Alert>
        </Box>
      </Box>
    </Box>
  )
}

export const budgetLimitsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('budgets.forms.new_budget.budget_limits.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <BudgetLimits {...props} />,
  isCompleted: () => true,
})
